import type { Document as ContentfulDocument } from "@contentful/rich-text-types";
import { INLINES } from "@contentful/rich-text-types";
import { documentToHtmlString } from "@contentful/rich-text-html-renderer";

import { Image as ContentfulImage, NewsEntry } from "../graphql";
import {
  ContentJsonEsportRewatchEntry,
  ContentJsonExternalLinkEntry,
  ContentJsonNewsEntry,
  ContentJsonVideoEntry,
  ContentJsonImageNewsEntry,
  EsportsNewsContent,
  EsportsSpecficAttributes,
  Image,
  NewsContent,
  NewsEntryCommon,
  EsportCarouselContent,
  LiveEsportCarouselContent,
  EsportCarouselEventContent,
  Button,
  VideoProvider,
  CarouselContent,
  CountdownTimerKind,
  CarouselPollOption,
  CarouselCommonContent,
  GameName,
  FullscreenContent,
  UrlButton,
  ArticleButton,
  Section,
  BridgeFunctionButton,
  SpotlightContent,
  HeroContent,
  SpotlightLiveEntry,
} from "../types";

import { GameResolutionSize, GameResolution } from "../gameSettings/types";

import { Locale } from "../locale";
import { allCountries } from "../countries";
import {
  EsportNewsEntry,
  EsportCarouselEntry,
  EventEntry,
  FullscreenEntry,
  PollBlock,
  EsportFullscreenEntry,
  UnifiedButton,
  EventCalendarEntry,
  ButtonTarget,
} from "../graphql/types";
import { convertNewsEntryBodyToDetails } from "./convertNewsEntryBody";
import { CarouselEventEntry } from "../graphql/types/newsEntryTypes/carouselEventEntry";
import { CarouselEventPollEntry } from "../graphql/types/newsEntryTypes/carouselEventPollEntry";
import { Targeting, TargetingWithoutExclude } from "../graphql/types/targeting";
import { CarouselEventProgressTrackerEntry } from "../graphql/types/newsEntryTypes/carouselEventProgressTrackerEntry";
import { ingameStore } from "../gameCommands";
import { gameSettings } from "../gameSettings";
import { CarouselEventPollTargetsEntry } from "../graphql/types/newsEntryTypes/carouselEventPollTargetsEntry";
import { RelatedMedia, RelatedMediaItem } from "../graphql/types/relatedMedia";
import { Spotlight } from "../graphql/types/spotlight";
import { LocalizedSpotlight } from "../graphql/types/newsEntryTypes/localizedSpotlight";
import { getProgressTracker } from "./progressTracker";
import { Hero } from "../graphql/types/hero";
import { LocalizedHero } from "../graphql/types/newsEntryTypes/localizedHero";
import { EsportSpotlight } from "../graphql/types/esportSpotlight";
import { getEventCalendar } from "./eventCalendar";

const ssoClientIds = {
  make: "5CC9647E-5A7F-4ED9-A5D7-6017BAD4F13A",
  eventPlatform: {
    brawlstars: "CE657724-A952-4F2F-B2AA-A4E712EB685B",
    clashofclans: "FFF18471-7B7F-4BBC-9826-7436EDEB01EA",
    clashroyale: "FAF920E8-2F96-49F2-A70C-BE199C622DDA",
  },
} as const;

/**
 * Transform a ButtonTarget into a Button with all the necessary fields
 * This is the core transformation logic used by both UnifiedButton and CalendarEventEntry
 */
export const transformButtonTarget = (
  gameName: GameName,
  buttonTarget: ButtonTarget | null | undefined,
  buttonMetadata: {
    id: string;
    label: string;
    variant: string;
    order: string;
  },
  newsEntryIds: string[],
  section: Section = "newsEntries",
): Button | undefined => {
  if (!buttonTarget) {
    return undefined;
  }

  switch (buttonTarget.type) {
    case "customUrl":
    case "deepLink": {
      const cta: UrlButton = {
        title: null,
        type: "url",
        urlType: "url",
        id: buttonMetadata.id,
        order: buttonMetadata.order,
        variant: buttonMetadata.variant,
        url: buttonTarget.configuration?.url ?? "",
        label: buttonMetadata.label,
      };

      return cta;
    }

    case "inboxArticle": {
      const id = buttonTarget.configuration?.articleId;

      if (!id || !newsEntryIds.includes(id)) {
        return;
      }

      const cta: ArticleButton = {
        title: null,
        type: "article",
        urlType: "article",
        id: buttonMetadata.id,
        order: buttonMetadata.order,
        variant: buttonMetadata.variant,
        label: buttonMetadata.label,
        url: {
          id,
          section,
        },
        article: {
          id,
          section,
        },
      };

      return cta;
    }

    case "inboxPoll": {
      const id = buttonTarget.configuration?.articleId;

      if (!id) {
        return;
      }

      const cta: ArticleButton = {
        title: null,
        type: "article",
        urlType: "article",
        id: buttonMetadata.id,
        order: buttonMetadata.order,
        variant: buttonMetadata.variant,
        label: buttonMetadata.label,
        url: {
          id,
          section: "carouselEventPollEntry",
        },
        article: {
          id,
          section: "carouselEventPollEntry",
        },
      };

      return cta;
    }

    case "ingameStore": {
      if (!ingameStore[gameName]) {
        return undefined;
      }

      const cta: BridgeFunctionButton = {
        title: null,
        type: "bridgeFunction",
        urlType: "bridgeFunction",
        id: buttonMetadata.id,
        order: buttonMetadata.order,
        variant: buttonMetadata.variant,
        functionName: "openShop",
        functionArgs: [],
        label: buttonMetadata.label,
      };

      return cta;
    }

    case "makeSso": {
      const url =
        buttonTarget.configuration?.url ?? "https://make.supercell.com";
      const urlWithSsoParams = `${url}${
        url.indexOf("?") !== -1 ? "&" : "?"
      }sso_client_id=${ssoClientIds.make}`;

      const cta: BridgeFunctionButton = {
        title: null,
        type: "bridgeFunction",
        urlType: "bridgeFunction",
        id: buttonMetadata.id,
        order: buttonMetadata.order,
        variant: buttonMetadata.variant,
        functionName:
          gameName === "clashofclans" ? "scidOpenWebSSO" : "openUrlWithSso",
        functionArgs: [urlWithSsoParams],
        label: buttonMetadata.label,
      };

      return cta;
    }

    case "eventPlatform": {
      if (
        gameName === "boombeach" ||
        gameName === "hayday" ||
        gameName === "rogue" ||
        gameName === "squadbusters"
      ) {
        return;
      }

      const url = `https://event.supercell.com/${gameName}`;
      const urlWithSsoParams = `${url}${
        url.indexOf("?") !== -1 ? "&" : "?"
        // pfg=true is important, it hides Supercell Store from the site
      }sso_client_id=${ssoClientIds.eventPlatform[gameName]}&pfg=true`;

      const cta: BridgeFunctionButton = {
        title: null,
        type: "bridgeFunction",
        urlType: "bridgeFunction",
        id: buttonMetadata.id,
        order: buttonMetadata.order,
        variant: buttonMetadata.variant,
        functionName:
          gameName === "clashofclans" ? "scidOpenWebSSO" : "openUrlWithSso",
        functionArgs: [urlWithSsoParams],
        label: buttonMetadata.label,
      };

      return cta;
    }

    // Deprecated (but we still have this type of content, so we need to support it)
    case "make": {
      const cta: UrlButton = {
        title: null,
        type: "url",
        urlType: "url",
        id: buttonMetadata.id,
        order: buttonMetadata.order,
        variant: buttonMetadata.variant,
        url: buttonTarget.configuration?.url ?? "https://make.supercell.com",
        label: buttonMetadata.label,
      };

      return cta;
    }
  }
};

export const transformUnifiedButton = (
  gameName: GameName,
  button: UnifiedButton,
  order: string,
  newsEntryIds: string[],
  section: Section = "newsEntries",
): Button | undefined => {
  return transformButtonTarget(
    gameName,
    button.buttonTarget,
    {
      id: button.sys.id,
      label: button.buttonText ?? "",
      variant:
        button.buttonColorVariant ??
        gameSettings[gameName]?.availableButtonVariants?.[0] ??
        "primary",
      order,
    },
    newsEntryIds,
    section,
  );
};

const getCarouselCtas = (
  gameName: GameName,
  localized:
    | CarouselEventEntry
    | CarouselEventProgressTrackerEntry
    | CarouselEventPollEntry
    | CarouselEventPollTargetsEntry
    | LocalizedSpotlight
    | LocalizedHero
    | EventCalendarEntry,
  newsEntryIds: string[],
) => {
  if (
    localized.__typename === "CarouselEventPollTargetsEntry" ||
    localized.__typename === "CarouselEventPollEntry"
  ) {
    return [];
  }

  const buttons =
    localized.__typename === "LocalizedSpotlight" ||
    localized.__typename === "LocalizedHero"
      ? localized.buttonsCollection?.items
      : localized.ctaButtonsCollection?.items;

  if (!buttons) {
    return [];
  }

  return buttons
    .map((button, index) => {
      return transformUnifiedButton(
        gameName,
        button,
        index.toString(),
        newsEntryIds,
      );
    })
    .filter((button): button is Button => button !== undefined);
};

export const getPollOptions = (
  localized: CarouselEventPollEntry | PollBlock,
  transformFunction: ReturnType<typeof transformImageWithFallback>,
  resolutions: GameResolution,
  resolutionsKey: keyof GameResolution,
): CarouselPollOption[] => {
  if ("pollOptions" in localized && Array.isArray(localized.pollOptions)) {
    return localized.pollOptions
      .map((option, index) => {
        return {
          id: (index + 1).toString(),
          title: option.title ?? "",
          image: transformFunction(
            {
              image: option.image,
              imageEn: option.image,
            },
            "image",
            resolutions[resolutionsKey],
          ),
        };
      })
      .filter(
        (option): option is CarouselPollOption =>
          Boolean(option.title) || Boolean(option.image),
      );
  }

  return [];
};

const reverseCountryMapping = Object.fromEntries(
  Object.entries(allCountries).map((a) => a.reverse()),
);

type NonNullableTargeting = {
  [K in keyof TargetingWithoutExclude]-?: NonNullable<
    TargetingWithoutExclude[K]
  >;
};

const calculateCountryFromTargeting = (targeting: Targeting): string[] => {
  const excludeCountry = targeting.countryExclude ?? [];
  const country = targeting.country ?? [];

  if (excludeCountry.length > 0) {
    const mapped = excludeCountry.map((c) => reverseCountryMapping[c] ?? c);

    const allCountriesSansExcluded = Object.keys(allCountries).filter(
      (m) => !mapped.includes(m),
    );

    return allCountriesSansExcluded;
  }

  return country.map((c) => reverseCountryMapping[c] ?? c);
};

export const transformTargeting = (
  targeting?: Targeting | null,
): NonNullableTargeting | null | undefined => {
  if (!targeting) {
    return targeting;
  }

  const country = calculateCountryFromTargeting(targeting);

  return {
    country,

    platform: (targeting.platform || []).map((platform) =>
      platform.toLowerCase(),
    ),

    builderHallLevel: targeting.builderHallLevel ?? [],
    townHallLevel: targeting.townHallLevel ?? [],
    campaignId: targeting.campaignId ?? "",
    avatarIds: targeting.avatarIds ?? [],
    hideFromYoungPlayer: targeting.hideFromYoungPlayer ?? false,
  };
};

export const transformFullscreenEntry =
  (
    transformFunction: ReturnType<typeof transformImageWithFallback>,
    resolutions: GameResolution,
    gameName: GameName,
    newsEntryIds: string[],
    locale: Locale,
  ) =>
  (v: FullscreenEntry | EsportFullscreenEntry): FullscreenContent | null => {
    if (!v.localized) {
      return null;
    }

    const type =
      v.__typename === "EsportFullscreenEntry"
        ? "esportFullscreenCarousel"
        : "fullscreenCarousel";

    const document = v.localized.body?.json as ContentfulDocument | undefined;

    const html = document ? transformRichText(document, gameName, locale) : "";

    const backgroundMedia = transformFunction(
      v.localized,
      "backgroundMedia",
      resolutions.fullscreenEntryCarousel,
    );

    const content: FullscreenContent = {
      id: v.sys.id,
      type,
      targeting: transformTargeting(v.targeting) ?? null,
      relatedMedia:
        transformRelatedMedia(v.relatedMedia, transformFunction) ?? null,

      readMoreButton: getButton(
        gameName,
        v.localized.readMoreButton,
        "1",
        newsEntryIds,
      ),

      slides: [
        {
          body: html,
          backgroundMedia,
          id: "1",
        },
      ],
    };

    return content;
  };

export const transformCarouselEntry =
  (
    transformFunction: ReturnType<typeof transformImageWithFallback>,
    locale: Locale,
    gameName: GameName,
    resolutions: GameResolution,
    newsEntryIds: string[],
  ) =>
  (v: EventEntry): CarouselContent | null => {
    if (!v.localized) {
      return null;
    }

    const commonContent: CarouselCommonContent = {
      id: v.sys.id,

      metadata: {
        id: v.sys.id ?? "",
        publishedAt: v.sys.publishedAt ?? undefined,
        publishedVersion: v.sys.publishedVersion ?? 0,
      },

      targeting: transformTargeting(v.targeting) ?? null,

      postDate: transformDate(v.sys.firstPublishedAt),
      sortDate: transformDate(v.sortDate ?? v.sys.firstPublishedAt),
      lang: locale,

      title: v.localized.entryTitle ?? "",
      ctas: getCarouselCtas(gameName, v.localized, newsEntryIds),

      relatedMedia:
        transformRelatedMedia(v.relatedMedia, transformFunction) ?? null,
    };

    switch (v.localized.__typename) {
      case "CarouselEventProgressTrackerEntry": {
        const maxProgress = v.localized.maxProgress;

        if (!maxProgress) {
          return null;
        }

        return {
          ...commonContent,
          ...getProgressTracker(
            v.localized,
            locale,
            gameName,
            transformFunction,
            resolutions,
          ),
          type: "progressTrackerEntry",
          background: transformFunction(
            v.localized,
            "carouselImage",
            resolutions.carouselEventProgressTrackerEntry,
          ),
        };
      }

      case "CarouselEventEntry": {
        return {
          ...commonContent,
          type: "eventEntry",
          displayTimer: !!v.localized?.countdownTimer,
          timerLabel: v.localized?.countdownTimer?.timerText ?? undefined,
          timerKind:
            (v.localized?.countdownTimer?.timerKind as CountdownTimerKind) ??
            undefined,
          timerTarget: transformDate(
            v.localized?.countdownTimer?.timerEndDate ?? null,
          ),
          hideTitle: v.localized.entryHideTitle ?? false,

          background: transformFunction(
            v.localized,
            "carouselImage",
            resolutions.carouselEventEntry,
          ),
        };
      }
      case "CarouselEventPollEntry": {
        return {
          ...commonContent,
          hideTitle: v.localized.entryHideTitle ?? false,

          background: transformFunction(
            v.localized,
            "carouselImage",
            resolutions.carouselEventEntry,
          ),
          type: "pollEntry",
          pollTitle: v.localized.pollTitle ?? "",
          options: getPollOptions(
            v.localized,
            transformFunction,
            resolutions,
            "carouselEventPollEntry",
          ),
        };
      }
      case "CarouselEventPollTargetsEntry": {
        return {
          ...commonContent,
          hideTitle: v.localized.entryHideTitle ?? false,

          background: transformFunction(
            v.localized,
            "carouselImage",
            resolutions.carouselEventEntry,
          ),
          type: "pollEntry",
          pollTitle: v.localized.pollTitle ?? "",
          pollDescription: v.localized.pollDescription ?? "",
          options: [],
          pollTargets: v.localized.pollTargets ?? [],
        };
      }
    }
  };

const getButton = (
  gameName: GameName,
  button?: UnifiedButton | null,
  order = "1",
  newsEntryIds: string[] = [],
): Button | undefined => {
  if (!button) {
    return;
  }

  switch (button.__typename) {
    case "UnifiedButton": {
      const cta = transformUnifiedButton(gameName, button, order, newsEntryIds);
      return cta;
    }
  }
};

export const transformEsportCarouselEntry =
  (
    transformFunction: ReturnType<typeof transformImageWithFallback>,
    _locale: Locale,
    resolutions: GameResolution,
    gameName: GameName,
    newsEntryIds: string[],
  ) =>
  (v: EsportCarouselEntry): EsportCarouselContent | null => {
    if (!v.localized) {
      return null;
    }

    switch (v.localized.__typename) {
      case "EsportLiveEventEntry": {
        const out: LiveEsportCarouselContent = {
          id: v.sys.id,
          type: "liveEntry",
          sortDate: v.sortDate ? new Date(v.sortDate).getTime() : null,
          postDate: new Date(v.sys.firstPublishedAt ?? Date.now()).getTime(),
          title: v.localized.title ?? "",
          subtitle: v.localized.entrySubtitle ?? "",
          eventPromotionName: v.localized.eventPromotionName ?? "",
          eventPromotionLogo: transformFunction(
            v.localized,
            "eventPromotionLogo",
            resolutions.esportLiveEventEntry,
          ),
          carouselImage: transformFunction(
            v.localized,
            "carouselImage",
            resolutions.esportLiveEventEntry,
          ),
          livestreamEndTime: v.localized.livestreamEndTime ?? "",
          livestreamStartTime: v.localized.livestreamStartTime ?? "",
          region: v.localized.region ?? undefined,
          readMoreButton: getButton(
            gameName,
            v.localized.readMoreButton,
            "1",
            newsEntryIds,
          ),
          subscribeButton: getButton(
            gameName,
            v.localized.subscribeButton,
            "1",
            newsEntryIds,
          ),
          watchLiveButton: getButton(
            gameName,
            v.localized.watchLiveButton,
            "1",
            newsEntryIds,
          ),
          targeting: transformTargeting(v.targeting) ?? null,
          relatedMedia:
            transformRelatedMedia(v.relatedMedia, transformFunction) ?? null,
        };

        return out;
      }

      case "EsportCarouselEventEntry": {
        const out: EsportCarouselEventContent = {
          id: v.sys.id,
          type: "eventEntry",
          sortDate: v.sortDate ? new Date(v.sortDate).getTime() : null,
          postDate: new Date(v.sys.firstPublishedAt ?? Date.now()).getTime(),
          title: v.localized.entryTitle ?? "",
          subtitle: v.localized.entrySubtitle ?? "",
          carouselImage: transformFunction(
            v.localized,
            "carouselImage",
            resolutions.esportCarouselEventEntry,
          ),
          ctaButtons: (
            v.localized.ctaButtonsCollection?.items?.map((button, ind) =>
              getButton(gameName, button, ind.toString(), newsEntryIds),
            ) ?? []
          ).filter((b): b is Button => Boolean(b)),
          targeting: transformTargeting(v.targeting) ?? null,
          relatedMedia:
            transformRelatedMedia(v.relatedMedia, transformFunction) ?? null,
        };

        return out;
      }
    }
  };

const domainToProvider: Record<string, VideoProvider> = {
  "twitch.com": "twitch",
  "twitch.tv": "twitch",
  "youtu.be": "youtube",
  "youtube.com": "youtube",
  "tiktok.com": "tiktok",
  "snapchat.com": "snapchat",
};

export const identifyVideoProvider = (
  url: string | null,
): VideoProvider | "" => {
  if (!url) {
    return "";
  }

  const lowercasedUrl = url.toLowerCase();
  const provider = Object.entries(domainToProvider).find(([domain]) =>
    lowercasedUrl.includes(domain),
  );
  return provider ? (provider[1] as VideoProvider) : "";
};

export const transformNewsEntry =
  (
    transformFunction: ReturnType<typeof transformImageWithFallback>,
    locale: Locale,
    resolutions: GameResolution,
    gameName: GameName,
    newsEntryIds: string[],
    section: Section,
    isEsportNewsEntry: boolean = false,
  ) =>
  (
    v: NewsEntry | EsportNewsEntry,
    index: number,
  ): NewsContent | EsportsNewsContent | null => {
    if (!v.localized) {
      return null;
    }

    const esportSpecificFields: EsportsSpecficAttributes | null =
      isEsportNewsEntry
        ? {
            section: "esportNewsEntries",
          }
        : null;

    const title =
      v.localized.__typename !== "EsportRewatchMatchEntry"
        ? v.localized.title
        : "";

    const commonData: NewsEntryCommon & Partial<EsportsSpecficAttributes> = {
      id: v.sys.id,
      index,

      targeting: transformTargeting(v.targeting) ?? null,

      metadata: {
        id: v.sys.id ?? "",
        publishedVersion: v.sys.publishedVersion ?? 0,
        publishedAt: v.sys.publishedAt ?? undefined,
      },

      postDate: transformDate(v.sortDate ?? v.sys.firstPublishedAt),
      lang: locale,
      section: "newsEntries" as const,

      title: title ?? "",
      categories:
        v.category && v.category.name && v.category.color
          ? [
              {
                title: v.category.name,
                color: v.category.color,
                secondaryColor: v.category.secondaryColor,
              },
            ]
          : [],

      newUntil: transformDate(v.newUntil),

      relatedMedia:
        transformRelatedMedia(v.relatedMedia, transformFunction) ?? null,

      ...(esportSpecificFields !== null ? esportSpecificFields : {}),
    };

    switch (v.localized.__typename) {
      case "SmallNewsEntryArticle": {
        const out: ContentJsonNewsEntry = {
          ...commonData,
          type: isEsportNewsEntry ? "esportNewsEntry" : "newsEntry",
          listingSize: "small",
          showInNewsFeed: v.localized.showInNewsFeed ?? "SHOW_IN_NEWS_FEED",
          thumbnailAlt: transformFunction(
            v.localized,
            "smallThumbnail",
            resolutions.smallNewsEntryArticle,
          ),
          heroImage: transformFunction(
            v.localized,
            "heroImage",
            resolutions.smallNewsEntryArticle,
          ),
          heroProgressTracker: v.localized.heroProgressTracker
            ? {
                type: "articleHeroProgressTracker" as const,
                ...getProgressTracker(
                  v.localized.heroProgressTracker,
                  locale,
                  gameName,
                  transformFunction,
                  resolutions,
                ),
                title: v.localized.heroProgressTracker.entryTitle ?? "",
                ctas: getCarouselCtas(
                  gameName,
                  v.localized.heroProgressTracker,
                  newsEntryIds,
                ),
                background: transformFunction(
                  v.localized.heroProgressTracker,
                  "carouselImage",
                  resolutions.smallNewsEntryArticle,
                ),
                relatedMedia:
                  transformRelatedMedia(v.relatedMedia, transformFunction) ??
                  null,
              }
            : undefined,
          details: convertNewsEntryBodyToDetails(
            v.localized,
            transformFunction,
            resolutions,
            gameName,
            v.sys.id,
            newsEntryIds,
            section,
            locale,
          ),
        };

        return out;
      }

      case "NewsEntryArticle": {
        const out: ContentJsonNewsEntry = {
          ...commonData,
          type: isEsportNewsEntry ? "esportNewsEntry" : "newsEntry",
          listingSize: "large",
          showInNewsFeed: v.localized.showInNewsFeed ?? undefined,
          thumbnail: transformFunction(
            v.localized,
            "largeThumbnail",
            resolutions.newsEntryArticle,
          ),
          heroImage: transformFunction(
            v.localized,
            "heroImage",
            resolutions.newsEntryArticle,
          ),
          heroProgressTracker: v.localized.heroProgressTracker
            ? {
                type: "articleHeroProgressTracker" as const,
                ...getProgressTracker(
                  v.localized.heroProgressTracker,
                  locale,
                  gameName,
                  transformFunction,
                  resolutions,
                ),
                title: v.localized.heroProgressTracker.entryTitle ?? "",
                ctas: getCarouselCtas(
                  gameName,
                  v.localized.heroProgressTracker,
                  newsEntryIds,
                ),
                background: transformFunction(
                  v.localized.heroProgressTracker,
                  "carouselImage",
                  resolutions.newsEntryArticle,
                ),
                relatedMedia:
                  transformRelatedMedia(v.relatedMedia, transformFunction) ??
                  null,
              }
            : undefined,
          details: convertNewsEntryBodyToDetails(
            v.localized,
            transformFunction,
            resolutions,
            gameName,
            v.sys.id,
            newsEntryIds,
            section,
            locale,
          ),
        };

        return out;
      }
      case "NewsEntryVideo": {
        const out: ContentJsonVideoEntry = {
          ...commonData,
          type: "videoNewsEntry",
          title: v.localized.title ?? "",
          creator: undefined,
          embed: {
            id: v.localized.sys.id,
            livepid: undefined,
            provider: identifyVideoProvider(v.localized.videoUrl),
            url: v.localized.videoUrl ?? "",
          },
          embedVideo: false,
          listingSize: "large",
          thumbnail: transformFunction(
            v.localized,
            "videoThumbnail",
            resolutions?.newsEntryVideo,
          ),
        };

        return out;
      }

      case "NewsEntryImage": {
        const out: ContentJsonImageNewsEntry = {
          ...commonData,

          type: "imageNewsEntry",
          thumbnail: transformFunction(
            v.localized,
            "imageNewsThumbnail",
            resolutions?.newsEntryImage,
          ),
        };
        return out;
      }

      case "SmallNewsEntryExternalLink": {
        const out: ContentJsonExternalLinkEntry = {
          ...commonData,
          type: "externalNewsEntry",
          title: v.localized.title ?? "",
          creator: undefined,
          url: v.localized.linkUrl ?? "",
          listingSize: "small",
          thumbnailAlt: transformFunction(
            v.localized,
            "linkThumbnail",
            resolutions?.smallNewsEntryExternalLink,
          ),
        };

        return out;
      }

      case "EsportRewatchMatchEntry": {
        const provider = identifyVideoProvider(v.localized.matchUrl);

        const out: ContentJsonEsportRewatchEntry = {
          id: commonData.id,
          lang: commonData.lang,
          postDate: commonData.postDate,
          section: "esportNewsEntries",
          type: "esportRewatchMatchEntry",

          playerOneName: v.localized.playerOneName ?? "",
          playerOneScore: v.localized.playerOneScore ?? undefined,

          playerOneImage: transformFunction(
            v.localized,
            "playerOneImage",
            resolutions?.esportRewatchMatchEntry,
          ),

          playerTwoName: v.localized.playerTwoName ?? "",
          playerTwoScore: v.localized.playerTwoScore ?? undefined,

          playerTwoImage: transformFunction(
            v.localized,
            "playerTwoImage",
            resolutions?.esportRewatchMatchEntry,
          ),

          matchThumbnail: transformFunction(
            v.localized,
            "matchThumbnail",
            resolutions?.esportRewatchMatchEntry,
          ),

          matchUrl: v.localized.matchUrl ?? undefined,

          provider:
            provider.length > 0 ? (provider as VideoProvider) : undefined,
        };

        return out;
      }

      case "NewsEntryExternalLink": {
        const out: ContentJsonExternalLinkEntry = {
          ...commonData,
          type: "externalNewsEntry",
          title: v.localized.title ?? "",
          creator: undefined,
          url: v.localized.linkUrl ?? "",
          listingSize: "large",
          thumbnail: transformFunction(
            v.localized,
            "linkThumbnail",
            resolutions?.newsEntryExternalLink,
          ),
        };

        return out;
      }
    }
  };

const inboxProtocol = /\w+-inbox:\/\//;

// Replaces:
// gamename-inbox://rest-of-the-url -> rest-of-the-url
// gamename://rest-of-the-url -> rest-of-the-url
const removeDeepLink = (gameName: GameName, uri: string) =>
  uri.replace(uri.replace(inboxProtocol, ""), `${gameName}-inbox://`);

const isDeepLink = (gameName: GameName, uri: string) => {
  if (inboxProtocol.test(uri)) {
    return true;
  }

  if (uri.startsWith(`${gameName}-inbox://`)) {
    return true;
  }

  return false;
};

// URLEncode does not remove the ' character, so we need to do it manually
const cleanLink = (link: string): string => {
  return link.replaceAll(`'`, `%27`);
};

const doubleEncodingGameNames: GameName[] = [
  "boombeach",
  "clashofclans",
  "hayday",
  "squadbusters",
  "rogue",
];

const tripleEncodingGameNames: GameName[] = ["brawlstars"];

export const transformRichText = (
  doc: ContentfulDocument,
  gameName: GameName,
  locale: Locale,
): string => {
  return documentToHtmlString(doc, {
    renderNode: {
      [INLINES.HYPERLINK]: (node, next) => {
        const cleanedLink = cleanLink(node.data.uri);

        if (node.data.uri.startsWith("#")) {
          return `<a href="${cleanedLink}">${next(node.content)}</a>`;
        }

        if (isDeepLink(gameName, node.data.uri)) {
          return `<a href="javascript:window.sendCommand('${encodeURIComponent(
            removeDeepLink(gameName, cleanedLink),
          )}')">${next(node.content)}</a>`;
        }

        // We replace all the contentful hyperlinks with a special anchor tag that calls the `OpenWeb` command of the game
        // This is done to ensure that all link handling is done in the game and not in the browser
        const encodedLink = tripleEncodingGameNames.includes(gameName)
          ? encodeURIComponent(
              encodeURIComponent(encodeURIComponent(cleanedLink)),
            )
          : doubleEncodingGameNames.includes(gameName)
            ? encodeURIComponent(encodeURIComponent(cleanedLink))
            : encodeURIComponent(cleanedLink);
        const out = `<a href="javascript:window.sendCommand('OpenWeb?url=${encodedLink}')">${next(
          node.content,
        )}</a>`;

        return out;
      },
      [INLINES.ENTRY_HYPERLINK]: (node) => {
        const { data, content } = node;

        if (!data?.target?.sys) {
          return "";
        }

        const { id } = data.target.sys;
        const langCode = locale;
        const linkText =
          content
            .map((child) => (child.nodeType === "text" ? child.value : ""))
            .join("") || "Link";

        const rawUrl = `#/${langCode}/news/${id}`;

        const cleanedLink = cleanLink(rawUrl);

        return `<a href="${cleanedLink}">${linkText}</a>`;
      },
    },
  });
};

export type NonNullableContentfulImage = {
  [K in keyof ContentfulImage]-?: NonNullable<ContentfulImage[K]>;
};

export const isValidContentfulImage = (
  img: ContentfulImage | null | undefined,
): img is NonNullableContentfulImage => {
  if (!img) {
    return false;
  }

  if (!img.url || !img.width || !img.height || !img.title) {
    return false;
  }

  return true;
};

// if the keys are changed, the CloudFront Cache Policy for Contentful must also be updated
type ContentfulValidImageParams = Record<
  "w" | "h" | "fit" | "f",
  string | number | undefined
>;

const constructImagePath = (
  url: string,
  size: { height: number | undefined; width: number },
  fit?: "default" | "fill" | "pad",
) => {
  const params: ContentfulValidImageParams = {
    w: size.width,
    h: size.height,
    f: "center",
    fit: fit === "default" ? undefined : fit ? fit : "fill",
  };

  const path = `${url}?${Object.entries(params)
    .filter(([_, v]) => v !== undefined)
    .map(([k, v]) => `${k}=${v}`)
    .join("&")}`;

  return path;
};

const MAX_CONTENTFUL_IMAGE_SIZE = 4000;

const calculateMaxRatio = (size: { height: number; width: number }) => {
  const { height, width } = size;
  const maxWidth = MAX_CONTENTFUL_IMAGE_SIZE;
  const maxHeight = MAX_CONTENTFUL_IMAGE_SIZE;

  // calc
  const widthRatio = maxWidth / width,
    heightRatio = maxHeight / height;

  const bestRatio = Math.min(widthRatio, heightRatio);

  // output
  const newWidth = Math.round(width * bestRatio);
  const newHeight = Math.round(height * bestRatio);

  return {
    height: newHeight,
    width: newWidth,
  };
};

export const transformImage =
  (cdnTransform: (url: string) => string) =>
  (
    img: ContentfulImage | null | undefined,
    resolution?: GameResolutionSize,
  ): Image | undefined => {
    if (!isValidContentfulImage(img)) {
      return;
    }

    const url = cdnTransform(img.url);

    const baseSize = resolution
      ? resolution
      : {
          width: img.width,
          height: img.height,
        };

    const smallSize = {
      width: Math.round(baseSize.width * 0.2),
      height: baseSize.height ? Math.round(baseSize.height * 0.2) : undefined,
    };

    const smallRetinaSize = {
      width: Math.round(baseSize.width * 0.4),
      height: baseSize.height ? Math.round(baseSize.height * 0.4) : undefined,
    };

    const mediumSize = {
      width: Math.round(baseSize.width * 0.5),
      height: baseSize.height ? Math.round(baseSize.height * 0.5) : undefined,
    };

    const mediumRetinaSize = {
      width: Math.round(baseSize.width * 0.6),
      height: baseSize.height ? Math.round(baseSize.height * 0.6) : undefined,
    };

    const largeSize = {
      width: Math.round(baseSize.width * 0.8),
      height: baseSize.height ? Math.round(baseSize.height * 0.8) : undefined,
    };

    const largeRetinaSize = {
      width: baseSize.width,
      height: baseSize.height,
    };

    const mapSize = (size: { height: number | undefined; width: number }) => {
      let properSize = {
        width: Math.max(size.width, 1),
        height: size.height ? Math.max(size.height, 1) : undefined,
      };

      if (
        properSize.width > MAX_CONTENTFUL_IMAGE_SIZE ||
        (properSize.height && properSize.height > MAX_CONTENTFUL_IMAGE_SIZE)
      ) {
        const maxRatio = calculateMaxRatio(img);
        properSize = {
          width: maxRatio.width,
          height: maxRatio.height,
        };
      }

      const out = {
        title: img.title,
        path: constructImagePath(url, properSize, resolution?.fit),
        width: properSize.width,
        height: properSize.height,
      };

      return out;
    };

    return {
      small: mapSize(smallSize),
      smallRetina: mapSize(smallRetinaSize),
      medium: mapSize(mediumSize),
      mediumRetina: mapSize(mediumRetinaSize),
      large: mapSize(largeSize),
      largeRetina: mapSize(largeRetinaSize),
    };
  };

export type ImageWithFallback<T extends string> = Record<
  T | `${T}En`,
  ContentfulImage | null | undefined
>;

export const transformImageWithFallback =
  (cdnTransform: (url: string) => string) =>
  <T extends string>(
    obj: ImageWithFallback<T> | null | undefined,
    key: T,
    gameResolutionSize?: {
      [key in T]?: { width: number; height: number | undefined } | null;
    },
  ): Image | undefined => {
    if (obj === null || obj === undefined) {
      return undefined;
    }

    const img = obj[key];
    const imgEn = obj[`${key}En`];

    if (isValidContentfulImage(img)) {
      return transformImage(cdnTransform)(img, gameResolutionSize?.[key]);
    }

    if (isValidContentfulImage(imgEn)) {
      return transformImage(cdnTransform)(imgEn, gameResolutionSize?.[key]);
    }

    return undefined;
  };

export function transformDate(date: string | null): number {
  return Math.floor((date === null ? new Date() : new Date(date)).getTime());
}

export const transformRelatedMedia = (
  relatedMedia: RelatedMedia | null | undefined,
  transformFunction: ReturnType<typeof transformImageWithFallback>,
): RelatedMediaItem[] => {
  if (!relatedMedia?.relatedMedia) {
    return [];
  }

  return relatedMedia.relatedMedia.map((item) => {
    const transformedImage = transformFunction(
      {
        image: item.image,
        imageEn: item.image,
      },
      "image",
    );

    return {
      id: item.id,
      title: item.title,
      label: item.label,
      image:
        transformedImage && item.image
          ? {
              id: item.image?.id,
              title: item.image?.title,
              url: transformedImage.large.path,
              width: transformedImage.large.width,
              height: transformedImage.large.height ?? item.image.height,
            }
          : undefined,
    };
  });
};

export const transformSpotlightEntry =
  (
    transformFunction: ReturnType<typeof transformImageWithFallback>,
    locale: Locale,
    gameName: GameName,
    resolutions: GameResolution,
    newsEntryIds: string[],
  ) =>
  (v: Spotlight | EsportSpotlight): SpotlightContent | null => {
    if (!v.localized) {
      return null;
    }

    const commonContent = {
      id: v.sys.id,
      size: v.size ?? "medium",
      video: v.video,
      postDate: transformDate(v.sortDate ?? v.sys.firstPublishedAt),
      newUntil: transformDate(v.newUntil),
      targeting: transformTargeting(v.targeting) ?? null,
      relatedMedia:
        transformRelatedMedia(v.relatedMedia, transformFunction) ?? null,
    };

    switch (v.localized.__typename) {
      case "LocalizedSpotlight":
        return {
          ...commonContent,
          type: "spotlightBasic",
          title: v.localized.title ?? "",
          background: transformFunction(
            v.localized,
            "image",
            resolutions.localizedSpotlight,
          ),
          ctas: getCarouselCtas(gameName, v.localized, newsEntryIds),
        };
      case "CarouselEventPollEntry":
        return {
          ...commonContent,
          type: "spotlightPoll",
          background: transformFunction(
            v.localized,
            "carouselImage",
            resolutions.localizedSpotlight,
          ),
          pollTitle: v.localized.pollTitle ?? "",
          options: getPollOptions(
            v.localized,
            transformFunction,
            resolutions,
            "carouselEventPollEntry",
          ),
        };
      case "CarouselEventProgressTrackerEntry":
        return {
          ...commonContent,
          ...getProgressTracker(
            v.localized,
            locale,
            gameName,
            transformFunction,
            resolutions,
          ),
          type: "spotlightProgressTracker",
          background: transformFunction(
            v.localized,
            "carouselImage",
            resolutions.localizedSpotlight,
          ),
          ctas: getCarouselCtas(gameName, v.localized, newsEntryIds),
          title: v.localized.entryTitle ?? "",
        };
      case "EsportLiveEventEntry": {
        const out: SpotlightLiveEntry = {
          ...commonContent,
          type: "spotlightLiveEntry",
          title: v.localized.title ?? "",
          subtitle: v.localized.entrySubtitle ?? "",
          eventPromotionName: v.localized.eventPromotionName ?? "",
          eventPromotionLogo: transformFunction(
            v.localized,
            "eventPromotionLogo",
            resolutions.esportLiveEventEntry,
          ),
          background: transformFunction(
            v.localized,
            "carouselImage",
            resolutions.localizedSpotlight,
          ),
          livestreamEndTime: v.localized.livestreamEndTime ?? "",
          livestreamStartTime: v.localized.livestreamStartTime ?? "",
          readMoreButton: getButton(
            gameName,
            v.localized.readMoreButton,
            "1",
            newsEntryIds,
          ),
          subscribeButton: getButton(
            gameName,
            v.localized.subscribeButton,
            "1",
            newsEntryIds,
          ),
          watchLiveButton: getButton(
            gameName,
            v.localized.watchLiveButton,
            "1",
            newsEntryIds,
          ),
        };

        return out;
      }
      case "EventCalendarEntry":
        return {
          ...commonContent,
          ...getEventCalendar(
            v.localized,
            gameName,
            transformFunction,
            resolutions,
            newsEntryIds,
          ),
          type: "spotlightEventCalendar",
          background: transformFunction(
            v.localized,
            "backgroundImage",
            resolutions.eventCalendarEntry,
          ),
          ctas: getCarouselCtas(gameName, v.localized, newsEntryIds),
          title: v.localized.entryTitle ?? "",
        };
      default:
        return null;
    }
  };

export const transformHeroEntry =
  (
    transformFunction: ReturnType<typeof transformImageWithFallback>,
    locale: Locale,
    gameName: GameName,
    resolutions: GameResolution,
    newsEntryIds: string[],
  ) =>
  (v: Hero): HeroContent | null => {
    if (!v.localized) {
      return null;
    }

    const commonContent = {
      id: v.sys.id,
      targeting: transformTargeting(v.targeting) ?? null,
      relatedMedia:
        transformRelatedMedia(v.relatedMedia, transformFunction) ?? null,
      type: "hero",
      ctas: getCarouselCtas(gameName, v.localized, newsEntryIds),
    };

    switch (v.localized.__typename) {
      case "LocalizedHero":
        return {
          ...commonContent,
          type: "heroBasic",
          background: transformFunction(
            v.localized,
            "image",
            resolutions.localizedHero,
          ),
          ctas: getCarouselCtas(gameName, v.localized, newsEntryIds),
        };
      case "CarouselEventProgressTrackerEntry":
        return {
          ...commonContent,
          ...getProgressTracker(
            v.localized,
            locale,
            gameName,
            transformFunction,
            resolutions,
          ),
          type: "heroProgressTracker",
          background: transformFunction(
            v.localized,
            "carouselImage",
            resolutions.localizedHero,
          ),
          ctas: getCarouselCtas(gameName, v.localized, newsEntryIds),
          title: v.localized.entryTitle ?? "",
        };
      case "EventCalendarEntry":
        return {
          ...commonContent,
          ...getEventCalendar(
            v.localized,
            gameName,
            transformFunction,
            resolutions,
            newsEntryIds,
          ),
          type: "heroEventCalendar",
          background: transformFunction(
            v.localized,
            "backgroundImage",
            resolutions.eventCalendarEntry,
          ),
          ctas: getCarouselCtas(gameName, v.localized, newsEntryIds),
          title: v.localized.entryTitle ?? "",
        };
      default:
        return null;
    }
  };
